import {useQuery} from '@tanstack/react-query';
import {Box, CircularProgress, Alert} from '@mui/material';
import apiCall from '../../../../utils/api';
import Indicator from '../../components/Indicator';
import Section from '../../components/Section';
import {IDashboardData, SurveySlug} from '../../utils/types';
import {buildApiKey, serializeSlug} from '../../utils/helpers';

interface Props {
    container: string;
    uoaId: string;
    surveyId: string;
}

const buildSurveySlugs = (surveyId: number): SurveySlug[] => [
    {
        numerator: {
            survey_id: surveyId,
            question: 'question3',
            value: 'Item 3,Item 4',
        },
        operation: 'match',
        key: 'question3_item-3-item-4',
        roman: '',
        title: 'Frequenza di fruizione del luogo a confronto con la media',
        tooltip:
            'Il grafico mostra la percentuale di fruitori del vostro progetto che ha visitato il luogo due o più volte nell’ultimo mese, a confronto con la media dello stesso valore per tutti i progetti. Il valore medio viene calcolato sommando le percentuali rilevate per ciascun progetto e dividendo il totale per il numero complessivo di progetti.',
        aggregateChildren: true,
    },
    {
        numerator: {
            survey_id: surveyId,
            question: 'question4',
            value: 'Item 3,Item 4',
        },
        operation: 'match',
        key: 'question4_item-3-item-4',
        roman: '',
        title: 'Interesse a tornare presso il luogo a confronto con la media',
        tooltip:
            'Il grafico mostra la percentuale di partecipanti del vostro progetto che ha espresso l’intenzione di tornare, a confronto con la media dello stesso valore per tutti i progetti. Il valore medio viene calcolato sommando le percentuali rilevate per ciascun progetto e dividendo il totale per il numero complessivo di progetti considerati.',
        aggregateChildren: true,
    },
];

const Dashboard3 = ({
    container,
    uoaId,
    surveyId,
}: Props) => {
    const {data, isLoading, isError} = useQuery<IDashboardData>({
        queryKey: [
            'luoghi-dashboard3',
            container,
            uoaId,
            surveyId,
        ],
        queryFn: async () => {
            const surveySlugs = buildSurveySlugs(Number(surveyId));

            const res = await apiCall({
                url: `containers/${container}/uoa/${uoaId}/dashboards/fsv`,
                method: 'POST',
                data: {
                    question_slugs: surveySlugs.map(serializeSlug),
                },
            });

            const result = {} as IDashboardData;

            for (const slug of surveySlugs) {
                const apiKey = buildApiKey(slug);
                result[slug.key] = res.data[apiKey] ?? {uoa: 0, average: 0};
            }

            return result;
        },
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    const surveySlugs = buildSurveySlugs(Number(surveyId));

    const sections = [
        {
            title: 'I. FREQUENZA DI FRUIZIONE DEL LUOGO',
            keys: ['question3_item-3-item-4'],
            maxOverride: 100,
        },
        {
            title: 'II. INTERESSE A TORNARE PRESSO IL LUOGO',
            keys: ['question4_item-3-item-4'],
            maxOverride: 100,
        },
    ];

    return (
        <Box>
            {sections.map((section) => (
                <Section
                    key={section.title}
                    title={section.title}
                >
                    {surveySlugs
                        .filter((s) => section.keys.includes(s.key))
                        .map((slug) => (
                            <Indicator
                                key={slug.key}
                                roman={slug.roman}
                                title={slug.title}
                                tooltip={slug.tooltip}
                                data={data[slug.key]}
                                maxOverride={section.maxOverride}
                            />
                        ))}
                </Section>
            ))}
        </Box>
    );
};

export default Dashboard3;
